import { useCallback, useEffect, useRef } from 'react';

/**
 * 메뉴 스크롤 기능 관리 훅
 * @param {string|null} menuScrollType - 'wrap' 또는 'scroll'
 * @param {React.RefObject} mainMenuRef - 메인 메뉴 ref
 * @param {Array} menuData - 메뉴 데이터 (변경 시 재초기화)
 * @param {string|null} menuAlign - 'left' 또는 'right'
 */
export const useMenuScroll = (menuScrollType, mainMenuRef, menuData = null, menuAlign = 'left') => {
  const cleanupRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const normalizedAlign = menuAlign === 'right' ? 'right' : 'left';

  // 메뉴 스크롤 기능 초기화
  const initMenuScrollButtons = useCallback(() => {
    const mainMenu = (mainMenuRef?.current?.mainMenuRef) || document.querySelector('.header-area .main-menu.main-menu-scroll');
    if (!mainMenu) return null;

    const nav = mainMenu.querySelector('nav');
    const leftBtn = mainMenu.querySelector('.main-menu-scroll-btn-left');
    const rightBtn = mainMenu.querySelector('.main-menu-scroll-btn-right');

    if (!nav || !leftBtn || !rightBtn) return null;

    const setInitialScrollPosition = () => {
      const maxScrollLeft = Math.max(0, nav.scrollWidth - nav.clientWidth);
      nav.scrollLeft = normalizedAlign === 'right' ? maxScrollLeft : 0;
    };

    const updateButtonVisibility = () => {
      // 메뉴가 아직 렌더링되지 않았거나 크기가 계산되지 않은 경우 재시도
      if (nav.scrollWidth === 0 || nav.clientWidth === 0) {
        return;
      }

      const scrollLeft = nav.scrollLeft;
      const scrollWidth = nav.scrollWidth;
      const clientWidth = nav.clientWidth;
      const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

      if (scrollWidth <= clientWidth) {
        leftBtn.classList.add('scroll-btn-hidden');
        rightBtn.classList.add('scroll-btn-hidden');
        return;
      }

      if (scrollLeft <= 0.5) {
        leftBtn.classList.add('scroll-btn-hidden');
      } else {
        leftBtn.classList.remove('scroll-btn-hidden');
      }

      if (scrollLeft >= maxScrollLeft - 1) {
        rightBtn.classList.add('scroll-btn-hidden');
      } else {
        rightBtn.classList.remove('scroll-btn-hidden');
      }
    };

    const scrollMenu = (direction) => {
      const scrollAmount = 200;
      const currentScroll = nav.scrollLeft;
      const scrollWidth = nav.scrollWidth;
      const clientWidth = nav.clientWidth;
      const maxScrollLeft = scrollWidth - clientWidth;

      let targetScroll = direction === 'left'
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;

      targetScroll = Math.max(0, Math.min(targetScroll, maxScrollLeft));

      if (targetScroll < 1) targetScroll = 0;
      if (maxScrollLeft > 0 && targetScroll >= maxScrollLeft - 1) targetScroll = maxScrollLeft;

      nav.scrollTo({ left: targetScroll, behavior: 'smooth' });
    };

    const handleLeftClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollMenu('left');
    };

    const handleRightClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollMenu('right');
    };

    leftBtn.addEventListener('click', handleLeftClick);
    rightBtn.addEventListener('click', handleRightClick);

    const handleScroll = () => {
      const scrollLeft = nav.scrollLeft;
      const scrollWidth = nav.scrollWidth;
      const clientWidth = nav.clientWidth;
      const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

      if (normalizedAlign === 'left' && scrollLeft > 0 && scrollLeft < 1) {
        nav.scrollLeft = 0;
      }
      if (maxScrollLeft > 0 && scrollLeft > maxScrollLeft - 1 && scrollLeft < maxScrollLeft + 1) {
        nav.scrollLeft = maxScrollLeft;
      }
      updateButtonVisibility();
    };

    nav.addEventListener('scroll', handleScroll, { passive: true });

    const handleResize = () => {
      updateButtonVisibility();
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // ResizeObserver를 사용하여 메뉴 크기 변경 감지
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        updateButtonVisibility();
      });
      resizeObserver.observe(nav);
      resizeObserverRef.current = resizeObserver;
    }

    // 초기 스크롤 위치 설정 (정렬 기준)
    setInitialScrollPosition();

    // 메뉴가 완전히 렌더링될 때까지 여러 번 체크
    const checkAndUpdate = (attempts = 0) => {
      if (attempts > 10) return; // 최대 10번 시도

      if (nav.scrollWidth === 0 || nav.clientWidth === 0) {
        // 아직 렌더링되지 않았으면 재시도
        requestAnimationFrame(() => {
          checkAndUpdate(attempts + 1);
        });
        return;
      }

      // 초기 스크롤 위치 보정 (정렬 기준)
      setInitialScrollPosition();

      updateButtonVisibility();

      // 추가로 한 번 더 확인 (레이아웃이 안정화될 때까지)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateButtonVisibility();
        });
      });
    };

    // 초기화 시도
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        checkAndUpdate();
      });
    });

    return () => {
      leftBtn.removeEventListener('click', handleLeftClick);
      rightBtn.removeEventListener('click', handleRightClick);
      nav.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [mainMenuRef, normalizedAlign]);

  // 메뉴 스크롤 타입 적용
  const applyMenuScrollType = useCallback((type) => {
    // 이전 cleanup 실행
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    const validTypes = ['wrap', 'scroll', 'fixedWithMore'];
    const targetType = validTypes.indexOf(type) >= 0 ? type : 'wrap';
    const applyAsScroll = targetType === 'scroll'; // fixedWithMore는 wrap처럼 적용(스크롤 버튼 없음)

    const mainMenu = (mainMenuRef?.current?.mainMenuRef) || document.querySelector('.header-area .main-menu');
    if (mainMenu) {
      if (applyAsScroll) {
        mainMenu.classList.add('main-menu-scroll');

        // 메뉴가 완전히 렌더링될 때까지 대기
        const initWithRetry = (attempts = 0) => {
          if (attempts > 20) return; // 최대 20번 시도

          const nav = mainMenu.querySelector('nav');
          if (!nav || nav.scrollWidth === 0 || nav.clientWidth === 0) {
            // 아직 렌더링되지 않았으면 재시도
            requestAnimationFrame(() => {
              initWithRetry(attempts + 1);
            });
            return;
          }

          // 메뉴가 준비되었으면 초기화
          cleanupRef.current = initMenuScrollButtons();
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            initWithRetry();
          });
        });
      } else {
        mainMenu.classList.remove('main-menu-scroll');
      }
    }
  }, [initMenuScrollButtons, mainMenuRef]);

  useEffect(() => {
    applyMenuScrollType(menuScrollType);

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [menuScrollType, menuData, normalizedAlign, applyMenuScrollType]);
};

